import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, Clock } from 'lucide-react';
import PublicHeader from '../../components/layout/PublicHeader';
import PublicFooter from '../../components/layout/PublicFooter';
import PublicCtaSection from '../../components/layout/PublicCtaSection';
import BlogRelatedPosts from '../../components/blog/BlogRelatedPosts';
import SEO from '../../components/seo/SEO';
import { useI18n } from '../../context/I18nContext';
import { getPostBySlug, getRelatedPosts, formatDate } from '../../lib/blog';
import { buildBreadcrumbSchema } from '../../lib/breadcrumbSchema';

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, tSeo, language } = useI18n();
  const lang = language === 'fr' ? 'fr' : 'en';

  const post = slug ? getPostBySlug(slug, lang) ?? getPostBySlug(slug) : undefined;
  const relatedPosts = slug ? getRelatedPosts(slug, lang) : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [slug]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.description,
    author: {
      '@type': 'Organization',
      name: post.author,
    },
    datePublished: post.date,
    publisher: {
      '@type': 'Organization',
      name: 'Leadsme',
      url: 'https://leadsme.app',
      logo: {
        '@type': 'ImageObject',
        url: 'https://leadsme.app/logo512.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://leadsme.app/blog/${post.slug}`,
    },
    inLanguage: 'fr',
  };

  const breadcrumbSchema = buildBreadcrumbSchema([
    { name: tSeo('landing.home'), url: 'https://leadsme.app/' },
    { name: tSeo('blog.nav'), url: 'https://leadsme.app/blog' },
    { name: post.title, url: `https://leadsme.app/blog/${post.slug}` },
  ]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO
        title={`${post.title} · ${tSeo('blog.postSeoSuffix')}`}
        description={post.description}
        canonical={`https://leadsme.app/blog/${post.slug}`}
        ogImage={post.ogImage}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <PublicHeader />

      <main className="flex-1 pt-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('blog.backToBlog')}
          </Link>
        </div>

        <header className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {post.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs font-medium text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full capitalize"
              >
                {tag}
              </span>
            ))}
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 leading-tight mb-4 tracking-tight">
            {post.title}
          </h1>
          <p className="text-base sm:text-lg text-slate-600 mb-6 leading-relaxed">{post.description}</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-400 border-t border-gray-100 pt-4">
            <span>{post.author}</span>
            <span className="hidden sm:inline text-gray-300" aria-hidden>·</span>
            <span>{formatDate(post.date, lang)}</span>
            <span className="hidden sm:inline text-gray-300" aria-hidden>·</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {t('blog.readingTime', { minutes: post.readingTime })}
            </span>
          </div>
        </header>

        <article
          className="blog-content max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-8"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        <BlogRelatedPosts posts={relatedPosts} lang={lang} />

        <PublicCtaSection
          titleKey={post.ctaTitleKey ?? 'landing.ctaTitle'}
          descriptionKey={post.ctaDescriptionKey ?? 'landing.ctaDescription'}
          buttonKey={post.ctaButtonKey ?? 'landing.ctaButton'}
          trustKey={post.ctaTrustKey ?? 'landing.trustSignup'}
        />
      </main>

      <PublicFooter />
    </div>
  );
};

export default BlogPostPage;
